import { parseInlineImages } from '../lib/inlineImages';
import { useImageLightbox } from './ImageLightbox';

/** Message body with inline images. Text renders exactly as before
 *  (pre-wrap); ![name](/api/attachments/file/…) references render as the
 *  image itself. Inside an ImageLightboxProvider a click opens the in-app
 *  pop-up viewer (arrows through every image on the page); outside one it
 *  falls back to opening the full-size file in a new tab. Anything else —
 *  including markdown pointing anywhere but our attachment endpoint —
 *  stays literal. */
export default function MessageContent({
  content,
  className = '',
}: {
  content: string;
  className?: string;
}) {
  const lightbox = useImageLightbox();
  const segments = parseInlineImages(content);
  return (
    <div className={`text-sm text-fg whitespace-pre-wrap leading-relaxed ${className}`}>
      {segments.map((seg, i) => {
        if (seg.type === 'text') return <span key={i}>{seg.text}</span>;
        const img = (
          <img
            src={seg.url}
            alt={seg.alt || 'attached image'}
            loading="lazy"
            className="max-w-full max-h-80 rounded-lg border border-line"
          />
        );
        return lightbox ? (
          <button
            key={i}
            type="button"
            onClick={() => lightbox.openImage(seg.url, seg.alt)}
            className="block my-1.5 w-fit"
            style={{ background: 'none', border: 'none', padding: 0, cursor: 'zoom-in' }}
            title={seg.alt ? `View ${seg.alt}` : 'View image'}
          >
            {img}
          </button>
        ) : (
          <a
            key={i}
            href={seg.url}
            target="_blank"
            rel="noreferrer"
            className="block my-1.5 w-fit"
            title={seg.alt ? `View ${seg.alt} full size` : 'View full size'}
          >
            {img}
          </a>
        );
      })}
    </div>
  );
}
